import React, { useState, useEffect, useMemo } from "react";
import { Checkbox, Spin } from "antd";
import { toast } from "react-toastify";
import { getAllProducts } from "../../../../actions/specManager";
import {
  filterSpecManagerProductsBySearch,
  applyGlobalspecCustomSnapshotToProduct,
} from "../utils/utils";
import SelectedProductList from "../shared/SelectedProductList";
import AllProductsList from "../shared/AllProductsList";

export default function UpdateProductListPanel({
  customer,
  identifierFields,
  selectedProducts,
  customSnapshotsRef,
  searchValue,
  onSelectedProductsChange,
}) {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const abortController = new AbortController();
    let cancelled = false;
    getAllProducts(abortController.signal, customer).then((response) => {
      if (cancelled) return;
      if (response.success) {
        setAllProducts(response.products);
      } else {
        toast.error(response.message);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [customer]);

  const selectedProductIds = useMemo(
    () => new Set(selectedProducts.map((productRow) => String(productRow.product_id))),
    [selectedProducts],
  );

  const filteredProducts = filterSpecManagerProductsBySearch(
    allProducts,
    searchValue,
    identifierFields,
  );

  const selectableFilteredProducts = filteredProducts.filter(
    (productRow) => !selectedProductIds.has(String(productRow.product_id)),
  );

  const selectAllChecked = filteredProducts.length > 0 && selectableFilteredProducts.length === 0;

  const mergeProductForSelection = (productRow) => {
    const productId = String(productRow.product_id);
    const source = allProducts.find((row) => String(row.product_id) === productId);
    const resolvedSource = source != null ? source : productRow;
    const snapshot = customSnapshotsRef.current.get(resolvedSource.product_id);
    return applyGlobalspecCustomSnapshotToProduct(resolvedSource, snapshot);
  };

  const handleAddProduct = (productRow) => {
    const productId = String(productRow.product_id);
    if (selectedProductIds.has(productId)) return;
    onSelectedProductsChange([...selectedProducts, mergeProductForSelection(productRow)]);
  };

  const handleRemoveProduct = (productId) => {
    const productToRemove = selectedProducts.find(
      (productRow) => String(productRow.product_id) === String(productId),
    );
    if (productToRemove.test_submitted) {
      toast.error("Cannot remove a product with submitted tests.");
      return;
    }
    onSelectedProductsChange(
      selectedProducts.filter(
        (productRow) => String(productRow.product_id) !== String(productId),
      ),
    );
  };

  const handleToggleChecked = (productRow, nextChecked) => {
    if (nextChecked) {
      handleAddProduct(productRow);
    }
  };

  const handleSelectAllChange = (event) => {
    if (!event.target.checked) return;
    const merged = selectableFilteredProducts.map(mergeProductForSelection);
    if (merged.length) {
      onSelectedProductsChange([...selectedProducts, ...merged]);
    }
  };

  return (
    <div className="UpdateProductList_Stacked">
      <div className="UpdateProductList_SelectedSection">
        <div className="UpdateProductList_SectionTitle">
          Selected Products (
          {selectedProducts.length}
          )
        </div>
        <div className="UpdateProductList_SelectedScroll">
          <SelectedProductList
            products={selectedProducts}
            identifierFields={identifierFields}
            loading={loading}
            onRemoveProduct={handleRemoveProduct}
          />
        </div>
      </div>

      <div className="UpdateProductList_AllSection">
        <div className="UpdateProductList_AllHeader">
          <div className="UpdateProductList_SectionTitle">All Products</div>
          <Checkbox
            checked={selectAllChecked}
            disabled={loading || selectableFilteredProducts.length === 0}
            onChange={handleSelectAllChange}
          >
            <span className="UpdateProductList_SelectAllLabel">Select All</span>
          </Checkbox>
        </div>
        <div className="UpdateProductList_AllScroll">
          {loading ? (
            <div className="UpdateProductList_Loading"><Spin /></div>
          ) : (
            <AllProductsList
              products={filteredProducts}
              identifierFields={identifierFields}
              loading={loading}
              selectedProductIds={selectedProductIds}
              onToggleChecked={handleToggleChecked}
            />
          )}
        </div>
      </div>
    </div>
  );
}
